import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { handleLogin } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import {
    ArrowRight, Crown, ShieldCheck, Sparkles, Filter, LineChart, Star,
} from "lucide-react";
import { toast } from "sonner";

const features = [
    { icon: Crown,       title: "Kings, Aristocrats, Contenders, Challengers", text: "Every U.S. dividend-growth stock, neatly categorised by streak length." },
    { icon: Filter,      title: "Surgical filters",   text: "Yield, payout ratio, DGR, 5Y CAGR, P/E, market cap, sector, industry — all in one panel." },
    { icon: LineChart,   title: "Stock detail pages", text: "Dividend history charts, growth streaks, key metrics — at a glance." },
    { icon: Star,        title: "Personal watchlist", text: "Save tickers you love, sync across devices, build your compounding short-list." },
];

export default function Landing() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [total, setTotal] = useState(null);

    useEffect(() => {
        api.get("/stocks/meta").then((r) => setTotal(r.data.total)).catch(() => {});
    }, []);

    const stats = [
        { label: "Companies tracked",  value: total ? `${total}` : "50+" },
        { label: "Decades of streaks", value: "1957–today" },
        { label: "Filter dimensions",  value: "11" },
    ];

    useEffect(() => {
        if (params.get("auth") === "failed") {
            toast.error("Sign-in failed. Please try again.");
        }
    }, [params]);

    useEffect(() => {
        if (user) navigate("/dashboard", { replace: true });
    }, [user, navigate]);

    return (
        <div className="relative overflow-hidden">
            {/* HERO */}
            <section className="relative">
                <div className="absolute inset-0 hero-grid" aria-hidden />
                <div className="absolute -top-32 right-[-10%] h-[480px] w-[480px] rounded-full bg-emerald-100 blur-3xl opacity-60" aria-hidden />
                <div className="absolute -bottom-40 left-[-10%] h-[480px] w-[480px] rounded-full bg-amber-100 blur-3xl opacity-50" aria-hidden />

                <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 pb-28 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 animate-fade-up">
                        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white/70 backdrop-blur px-3 py-1 text-xs font-medium text-muted-foreground">
                            <Sparkles className="h-3.5 w-3.5 text-primary" />
                            Built for dividend growth investors
                        </div>
                        <h1 className="font-heading mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                            Find dividend growth<br />
                            stocks <span className="text-primary">worth holding</span>
                            <br />for the next 30 years.
                        </h1>
                        <p className="mt-6 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">
                            A focused screener for <strong className="text-foreground">Dividend Kings, Aristocrats, Contenders and Challengers.</strong> Filter by yield, payout ratio, DGR, growth streak and more.
                            No noise, no day-trader fluff — just durable compounders.
                        </p>

                        <div className="mt-10 flex flex-wrap items-center gap-3">
                            <Button
                                size="lg"
                                onClick={handleLogin}
                                className="rounded-full px-6 h-12 text-base shadow-sm"
                                data-testid="hero-login-btn"
                            >
                                Sign in with Google
                                <ArrowRight className="h-4 w-4" />
                            </Button>
                            <a href="#how" className="text-sm font-medium text-muted-foreground hover:text-foreground px-4 py-3">
                                How it works →
                            </a>
                        </div>

                        <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
                            {stats.map((s) => (
                                <div key={s.label}>
                                    <div className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{s.value}</div>
                                    <div className="overline mt-1">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* MOCK SCREENER PREVIEW */}
                    <div className="lg:col-span-5 animate-fade-up [animation-delay:120ms]">
                        <div className="rounded-2xl border border-border bg-white shadow-[0_8px_32px_-12px_rgba(17,24,39,0.12)] overflow-hidden">
                            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/50">
                                <div className="flex items-center gap-2">
                                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                                    <span className="text-xs font-semibold text-muted-foreground">Live screener preview</span>
                                </div>
                                <span className="text-xs text-muted-foreground">{total ? `${total} tickers` : "50+ tickers"}</span>
                            </div>
                            <table className="w-full text-sm">
                                <thead className="text-xs text-muted-foreground border-b border-border">
                                    <tr>
                                        <th className="text-left p-3 font-medium">Ticker</th>
                                        <th className="text-left p-3 font-medium">Cat.</th>
                                        <th className="text-right p-3 font-medium">Yield</th>
                                        <th className="text-right p-3 font-medium">Streak</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[
                                        ["KO",   "King",       "2.95%", "62y"],
                                        ["PG",   "King",       "2.51%", "68y"],
                                        ["JNJ",  "King",       "3.15%", "62y"],
                                        ["ADP",  "Aristocrat", "2.00%", "49y"],
                                        ["O",    "Aristocrat", "5.65%", "30y"],
                                        ["MSFT", "Challenger", "0.77%", "22y"],
                                    ].map(([t, c, y, st]) => (
                                        <tr key={t} className="border-b border-border last:border-0">
                                            <td className="p-3 font-semibold font-heading">{t}</td>
                                            <td className="p-3 text-muted-foreground">{c}</td>
                                            <td className="p-3 text-right tabular-nums">{y}</td>
                                            <td className="p-3 text-right tabular-nums text-primary font-semibold">{st}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-4 inline-flex items-center gap-2 text-xs text-muted-foreground">
                            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                            Seed data shown. Sign in to unlock full screener and watchlist.
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section id="how" className="border-t border-border bg-muted/40">
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24">
                    <div className="max-w-2xl">
                        <div className="overline text-primary">What you get</div>
                        <h2 className="font-heading mt-3 text-3xl sm:text-4xl font-bold tracking-tight">
                            One screener. Built for compounders.
                        </h2>
                        <p className="mt-4 text-muted-foreground leading-relaxed">
                            Divly is opinionated. No penny stocks, no meme tickers — only companies with proven, multi-decade
                            track records of raising their dividend.
                        </p>
                    </div>

                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {features.map((f) => (
                            <div key={f.title} className="rounded-2xl border border-border bg-background p-6 lift">
                                <div className="h-10 w-10 rounded-xl bg-accent text-accent-foreground grid place-items-center">
                                    <f.icon className="h-5 w-5" />
                                </div>
                                <h3 className="mt-5 font-heading font-semibold text-lg leading-snug">{f.title}</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="border-t border-border">
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
                        Start screening in 30 seconds.
                    </h2>
                    <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
                        Free to use. Sign in with Google to save your watchlist and personalise filters.
                    </p>
                    <Button
                        size="lg"
                        onClick={handleLogin}
                        className="mt-8 rounded-full px-6 h-12 text-base"
                        data-testid="cta-login-btn"
                    >
                        Sign in with Google
                        <ArrowRight className="h-4 w-4" />
                    </Button>
                    <div className="mt-10 text-xs text-muted-foreground">
                        © {new Date().getFullYear()} Divly · Built for dividend growth investors
                    </div>
                </div>
            </section>
        </div>
    );
}
